/**
 * Speech Recording Types
 * TypeScript interfaces for speech-to-text recording functionality
 * Aligned with Firebase Functions trigger-based architecture
 */

import { Timestamp } from 'firebase/firestore';

/**
 * Recording state machine - matches backend workflow
 */
export type RecordingState = 
  | 'idle'          // Ready to start recording
  | 'preparing'     // Microphone initializing
  | 'recording'     // Currently recording audio
  | 'uploading'     // Uploading to Firebase Storage
  | 'processing'    // Backend processing (waiting for Firestore update)
  | 'done'          // Transcription complete
  | 'error';        // Error occurred

/**
 * Base recording document structure
 * Path: /users/{userId}/recordings/{recordingId}
 */
export interface BaseRecordingDocument {
  id: string;                    // recordingId (UUID)
  userId: string;                // Current user ID
  createdAt: Timestamp;          // firebase.firestore.FieldValue.serverTimestamp()
  storage_path: string;          // Storage path for backend reference
  metadata?: {                   // Optional MediaRecorder info
    browser?: string;            // navigator.userAgent
    mimeType?: string;           // MediaRecorder.mimeType
    sampleRate?: number;         // AudioContext.sampleRate if available
  };
}

/**
 * Recording document structure (Frontend creates)
 */
export interface RecordingDocument extends BaseRecordingDocument {
  status: 'processing';          // Initial status
}

/**
 * Processed recording document (Backend updates)
 */
export interface ProcessedRecordingDocument extends BaseRecordingDocument {
  status: 'processing' | 'done' | 'error';  // All possible statuses
  transcribedText?: string;            // Speech-to-text result (on success)
  processedAt?: Timestamp;             // Processing completion time
  error?: string;                      // Error details (for debugging)
  processingDuration?: number;         // Processing time in ms
  updatedAt?: Timestamp;               // Last update timestamp
}

/**
 * Error codes for Firebase Functions error handling
 */
export type ErrorCode = 
  | 'NO_SPEECH_DETECTED'           // No speech content in audio
  | 'API_QUOTA_EXCEEDED'           // OpenAI API limits reached
  | 'PROCESSING_TIMEOUT'           // Processing took too long
  | 'UNSUPPORTED_FORMAT'           // Audio format not supported
  | 'FILE_TOO_LARGE'              // Audio file > 25MB
  | 'FILE_NOT_FOUND'              // Audio file not found in Storage
  | 'PROCESSING_ERROR'            // General processing error
  | 'UNEXPECTED_ERROR';           // Unexpected server error

/**
 * Recording hook return interface
 */
export interface UseSpeechRecordingReturn {
  // State
  recordingState: RecordingState;
  isRecording: boolean;
  isPreparing: boolean;             // Check if preparing
  recordingDuration: number;        // Duration in seconds
  transcribedText: string | null;
  error: string | null;
  
  // Actions
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<void>;
  clearError: () => void;
  clearTranscribedText: () => void;
  
  // Browser support
  isSupported: boolean;
}

/**
 * Audio recording constraints for optimal speech recognition
 * Mobile-friendly approach: let browser choose optimal settings
 */
export interface AudioRecordingConstraints {
  audio: {
    echoCancellation: boolean; // Reduce background noise
    noiseSuppression: boolean; // Improve audio quality
  };
}

/**
 * MediaRecorder options with browser compatibility
 */
export interface MediaRecorderOptions {
  mimeType: string;            // Primary: 'audio/webm;codecs=opus', fallback: 'audio/wav'
  audioBitsPerSecond: number;  // Good quality/size balance
}

/**
 * Recording configuration constants
 */
export const RECORDING_CONFIG = {
  MAX_DURATION_MS: 5 * 60 * 1000,     // 5 minutes
  MAX_FILE_SIZE_MB: 25,                // OpenAI Whisper limit
  AUDIO_BITS_PER_SECOND: 128000,      // Good quality/size balance
  MICROPHONE_READY_DELAY_MS: 300,     // Hardware warmup delay after MediaRecorder.onstart
  
  // Supported MIME types in order of preference
  SUPPORTED_MIME_TYPES: [
    'audio/webm;codecs=opus',  // Chrome, Firefox (preferred)
    'audio/webm',              // Chrome, Firefox (fallback)
    'audio/mp4',               // Safari (desktop and mobile)
    'audio/wav'                // Universal fallback
  ],
  
  // Audio constraints - mobile-friendly approach
  AUDIO_CONSTRAINTS: {
    echoCancellation: true,    // Essential for quality
    noiseSuppression: true,    // Essential for quality
    // Let browser choose optimal sampleRate and channelCount for device compatibility
  },
  
  // Minimal fallback constraints for problematic devices
  MINIMAL_AUDIO_CONSTRAINTS: {
    // Absolute minimum - no processing constraints that might fail on mobile
  }
} as const;